import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import type { ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Fade
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQueryClient } from '@tanstack/react-query';
import { bffApiClient } from '@api/bff/client';
import { TestPointRow } from '../TestPointRow';
import { useDatasheetPreviewQuery } from '../../hooks/useTestRuns';
import type { Script, TestPointPreview } from '../../types';
import type { ItemRef, ItemScriptData, ScriptStep } from '../../types/steps';
import type { SaveStatus } from '../../hooks/useItemAutoSave';

const pointKey = (functionId: string, label: string): string =>
  `${functionId}:${label}`.toLowerCase();

const parseTestPointRefKey = (scriptData: string | null): string | null => {
  if (!scriptData) return null;
  try {
    const parsed = JSON.parse(scriptData) as ItemScriptData;
    if (!parsed.testPointRef) return null;
    return pointKey(parsed.testPointRef.functionId, parsed.testPointRef.label);
  } catch {
    return null;
  }
};

export type StepClipboard = {
  sourceLabel: string;
  sourceScriptId: string;
  beforeSteps: ScriptStep[];
  calibrationSteps: ScriptStep[];
  afterSteps: ScriptStep[];
} | null;

type Props = {
  items: ItemRef[];
  childScripts: Script[];
  parentScriptId: string;
  onAddItems: () => void;
  onDuplicateItem: (item: Script) => void;
  onRemoveItem: (scriptId: string) => void;
  onRemoveItems?: (ids: string[]) => void;
  onReorder: (activeId: string, overId: string) => void;
  onItemSaveStatusesChange?: (statuses: SaveStatus[]) => void;
};

type SortableRowProps = {
  scriptId: string;
  children: ReactNode;
};

const SortableRow = ({ scriptId, children }: SortableRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: scriptId });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </Box>
  );
};

const buildStepSummary = (clipboard: StepClipboard): string => {
  if (!clipboard) return '';
  const parts: string[] = [];
  if (clipboard.beforeSteps.length > 0)
    parts.push(`${clipboard.beforeSteps.length} before`);
  if (clipboard.calibrationSteps.length > 0)
    parts.push(`${clipboard.calibrationSteps.length} cal`);
  if (clipboard.afterSteps.length > 0)
    parts.push(`${clipboard.afterSteps.length} after`);
  return parts.join(', ');
};

const parseStepsFromScript = (
  script: Script
): { hasSteps: boolean; label: string } => {
  if (!script.scriptData) return { hasSteps: false, label: script.globalId };
  try {
    const parsed = JSON.parse(script.scriptData) as ItemScriptData;
    const count =
      (parsed.beforeSteps ?? []).length +
      (parsed.calibrationSteps ?? []).length +
      (parsed.afterSteps ?? []).length;
    const ref = parsed.testPointRef;
    const label = ref
      ? `${ref.functionId.toUpperCase()} — ${ref.nominal} ${ref.unit}${ref.frequency ? ` @ ${ref.frequency} Hz` : ''}`
      : script.globalId;
    return { hasSteps: count > 0, label };
  } catch {
    return { hasSteps: false, label: script.globalId };
  }
};

const doPasteToTarget = async (
  target: Script,
  clipboard: StepClipboard,
  queryClient: ReturnType<typeof useQueryClient>,
  parentScriptId: string
) => {
  if (!clipboard || !target.scriptData) return;

  const parsed = JSON.parse(target.scriptData) as ItemScriptData;
  const newData: ItemScriptData = {
    testPointRef: parsed.testPointRef,
    beforeSteps: structuredClone(clipboard.beforeSteps),
    calibrationSteps: structuredClone(clipboard.calibrationSteps),
    afterSteps: structuredClone(clipboard.afterSteps)
  };

  const response = await bffApiClient.put<Script>(
    `/api/mqtt-scripts/${target.id}`,
    {
      globalId: target.globalId,
      scriptType: target.scriptType,
      scriptData: JSON.stringify(newData),
      createdBy: target.createdBy
    }
  );

  queryClient.setQueryData(['scripts', target.id], response.data);
  queryClient.setQueryData<Script[]>(
    ['scripts', 'children', parentScriptId],
    old => old?.map(child => (child.id === target.id ? response.data : child))
  );
};

type BulkPasteDialogProps = {
  open: boolean;
  clipboard: StepClipboard;
  allScripts: Script[];
  sourceScriptId: string;
  isPasting: boolean;
  onClose: () => void;
  onPaste: (targetIds: string[]) => void;
};

const BulkPasteDialog = ({
  open,
  clipboard,
  allScripts,
  sourceScriptId,
  isPasting,
  onClose,
  onPaste
}: BulkPasteDialogProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setShowOverwriteConfirm(false);
    }
  }, [open]);

  const targets = useMemo(
    () =>
      allScripts.filter(s => s.id !== sourceScriptId).map(s => {
        const { hasSteps, label } = parseStepsFromScript(s);
        return { script: s, hasSteps, label };
      }),
    [allScripts, sourceScriptId]
  );

  const allTargetIds = useMemo(() => targets.map(t => t.script.id), [targets]);
  const emptyTargetIds = useMemo(
    () => targets.filter(t => !t.hasSteps).map(t => t.script.id),
    [targets]
  );

  const handleToggle = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectedWithSteps = useMemo(
    () =>
      targets.filter(t => selectedIds.has(t.script.id) && t.hasSteps).length,
    [targets, selectedIds]
  );

  const handleConfirmPaste = useCallback(() => {
    if (selectedWithSteps > 0 && !showOverwriteConfirm) {
      setShowOverwriteConfirm(true);
      return;
    }
    onPaste(Array.from(selectedIds));
    setShowOverwriteConfirm(false);
  }, [selectedIds, selectedWithSteps, showOverwriteConfirm, onPaste]);

  if (!clipboard) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {showOverwriteConfirm ? 'Confirm Overwrite' : 'Paste Steps to Test Points'}
      </DialogTitle>
      <DialogContent>
        {showOverwriteConfirm ? (
          <DialogContentText>
            {selectedWithSteps} of {selectedIds.size} selected test point
            {selectedIds.size !== 1 ? 's' : ''} already ha
            {selectedWithSteps !== 1 ? 've' : 's'} existing steps that will be
            overwritten. Continue?
          </DialogContentText>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Select target test points to receive steps from{' '}
              <strong>{clipboard.sourceLabel}</strong>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedIds(new Set(allTargetIds))}
              >
                Select All
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedIds(new Set(emptyTargetIds))}
              >
                Select All Empty
              </Button>
              {selectedIds.size > 0 && (
                <Button size="small" onClick={() => setSelectedIds(new Set())}>
                  Clear
                </Button>
              )}
            </Box>
            {targets.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 4 }}
              >
                No other test points available as paste targets.
              </Typography>
            ) : (
              targets.map(target => (
                <Box
                  key={target.script.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(target.script.id)}
                    onChange={() => handleToggle(target.script.id)}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {target.label}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPasting}>
          Cancel
        </Button>
        {showOverwriteConfirm ? (
          <>
            <Button
              onClick={() => setShowOverwriteConfirm(false)}
              disabled={isPasting}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleConfirmPaste}
              disabled={isPasting}
            >
              {isPasting ? 'Pasting...' : 'Overwrite'}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={handleConfirmPaste}
            disabled={selectedIds.size === 0 || isPasting}
          >
            {isPasting
              ? 'Pasting...'
              : `Paste to ${selectedIds.size} test point${selectedIds.size !== 1 ? 's' : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const TestPointsSection = ({
  items,
  childScripts,
  parentScriptId,
  onAddItems,
  onDuplicateItem,
  onRemoveItem,
  onRemoveItems,
  onReorder,
  onItemSaveStatusesChange
}: Props) => {
  const queryClient = useQueryClient();
  const itemStatusMap = useRef(new Map<string, SaveStatus>());

  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false);
  const [clipboard, setClipboard] = useState<StepClipboard>(null);
  const [pasteTarget, setPasteTarget] = useState<Script | null>(null);
  const [bulkPasteOpen, setBulkPasteOpen] = useState(false);
  const [isPasting, setIsPasting] = useState(false);

  const orderedScripts = useMemo(
    () =>
      items
        .map(ref => childScripts.find(s => s.id === ref.scriptId))
        .filter((s): s is Script => !!s),
    [items, childScripts]
  );

  const { data: preview } = useDatasheetPreviewQuery(parentScriptId);

  const previewByScriptId = useMemo(() => {
    const map = new Map<string, TestPointPreview>();
    if (!preview) return map;

    const byPoint = new Map<string, TestPointPreview>();
    for (const group of preview.groups) {
      for (const rp of group.resolvedPoints ?? []) {
        byPoint.set(pointKey(rp.functionId, rp.label), {
          resolved: rp,
          blockedReason: null
        });
      }
      for (const bp of group.blockedPoints ?? []) {
        byPoint.set(pointKey(bp.detail.functionId, bp.label), {
          resolved: null,
          blockedReason: bp.reasonCode
        });
      }
    }

    for (const script of orderedScripts) {
      const key = parseTestPointRefKey(script.scriptData);
      if (!key) continue;
      const info = byPoint.get(key);
      if (info) map.set(script.id, info);
    }
    return map;
  }, [preview, orderedScripts]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        onReorder(String(active.id), String(over.id));
      }
    },
    [onReorder]
  );

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedRowId(prev => (prev === id ? null : id));
  }, []);

  const handleConfirmRemove = useCallback(() => {
    if (removeTarget) {
      onRemoveItem(removeTarget);
      setRemoveTarget(null);
      if (expandedRowId === removeTarget) {
        setExpandedRowId(null);
      }
    }
  }, [removeTarget, onRemoveItem, expandedRowId]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev =>
      prev.size === orderedScripts.length
        ? new Set()
        : new Set(orderedScripts.map(s => s.id))
    );
  }, [orderedScripts]);

  const handleConfirmBulkRemove = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (onRemoveItems) {
      onRemoveItems(ids);
    } else {
      for (const id of ids) {
        onRemoveItem(id);
      }
    }
    setSelectedIds(new Set());
    setBulkRemoveOpen(false);
    setExpandedRowId(null);
  }, [selectedIds, onRemoveItem, onRemoveItems]);

  const handleCopy = useCallback((item: Script) => {
    if (!item.scriptData) return;
    try {
      const parsed = JSON.parse(item.scriptData) as ItemScriptData;
      const ref = parsed.testPointRef;
      const label = ref
        ? `${ref.functionId.toUpperCase()} — ${ref.nominal} ${ref.unit}${ref.frequency ? ` @ ${ref.frequency} Hz` : ''}`
        : item.globalId;

      setClipboard({
        sourceLabel: label,
        sourceScriptId: item.id,
        beforeSteps: structuredClone(parsed.beforeSteps ?? []),
        calibrationSteps: structuredClone(parsed.calibrationSteps ?? []),
        afterSteps: structuredClone(parsed.afterSteps ?? [])
      });
    } catch {
      // noop
    }
  }, []);

  const handlePasteClick = useCallback(
    (item: Script) => {
      const { hasSteps } = parseStepsFromScript(item);
      if (hasSteps) {
        setPasteTarget(item);
      } else {
        setIsPasting(true);
        doPasteToTarget(item, clipboard, queryClient, parentScriptId).finally(
          () => setIsPasting(false)
        );
      }
    },
    [clipboard, queryClient, parentScriptId]
  );

  const handleConfirmPaste = useCallback(() => {
    if (!pasteTarget) return;
    setIsPasting(true);
    doPasteToTarget(pasteTarget, clipboard, queryClient, parentScriptId).finally(
      () => {
        setIsPasting(false);
        setPasteTarget(null);
      }
    );
  }, [pasteTarget, clipboard, queryClient, parentScriptId]);

  const handleBulkPaste = useCallback(
    (targetIds: string[]) => {
      setIsPasting(true);
      const targets = childScripts.filter(s => targetIds.includes(s.id));
      Promise.all(
        targets.map(t =>
          doPasteToTarget(t, clipboard, queryClient, parentScriptId)
        )
      ).finally(() => {
        setIsPasting(false);
        setBulkPasteOpen(false);
      });
    },
    [childScripts, clipboard, queryClient, parentScriptId]
  );

  const handleItemSaveStatusChange = useCallback(
    (itemId: string, status: SaveStatus) => {
      itemStatusMap.current.set(itemId, status);
      onItemSaveStatusesChange?.(Array.from(itemStatusMap.current.values()));
    },
    [onItemSaveStatusesChange]
  );

  const clipboardSummary = useMemo(
    () => buildStepSummary(clipboard),
    [clipboard]
  );

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1
          }}
        >
          <Typography variant="overline" color="text.secondary">
            Test Points ({orderedScripts.length})
          </Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAddItems}>
            Add Test Points
          </Button>
        </Box>

        {selectedIds.size > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={() => setBulkRemoveOpen(true)}
            >
              Remove {selectedIds.size} selected
            </Button>
            <Button size="small" onClick={() => setSelectedIds(new Set())}>
              Clear selection
            </Button>
          </Box>
        )}

        {orderedScripts.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              border: 1,
              borderColor: 'divider',
              borderStyle: 'dashed',
              borderRadius: 1
            }}
          >
            <InboxOutlinedIcon
              sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No test points yet.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onAddItems}>
              Add Test Points
            </Button>
          </Box>
        ) : (
          <>
            {orderedScripts.length > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 0.5,
                  px: 0.5
                }}
              >
                <Checkbox
                  size="small"
                  checked={selectedIds.size === orderedScripts.length}
                  indeterminate={
                    selectedIds.size > 0 &&
                    selectedIds.size < orderedScripts.length
                  }
                  onChange={toggleSelectAll}
                />
                <Typography variant="caption" color="text.secondary">
                  Select all ({orderedScripts.length})
                </Typography>
              </Box>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedScripts.map(s => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {orderedScripts.map((item, index) => (
                  <SortableRow key={item.id} scriptId={item.id}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleSelected(item.id)}
                        sx={{ mt: 0.5 }}
                        onClick={e => e.stopPropagation()}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TestPointRow
                          item={item}
                          index={index}
                          isExpanded={expandedRowId === item.id}
                          onToggleExpand={() => handleToggleExpand(item.id)}
                          onRemove={() => setRemoveTarget(item.id)}
                          onSaveStatusChange={handleItemSaveStatusChange}
                          onDuplicate={() => onDuplicateItem(item)}
                          onCopy={() => handleCopy(item)}
                          clipboard={clipboard}
                          onPaste={() => handlePasteClick(item)}
                          preview={previewByScriptId.get(item.id)}
                          parentScriptId={parentScriptId}
                        />
                      </Box>
                    </Box>
                  </SortableRow>
                ))}
              </SortableContext>
            </DndContext>

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={onAddItems}
              sx={{ mt: 1, alignSelf: 'flex-start' }}
            >
              Add Test Points
            </Button>
          </>
        )}

        <Fade in={!!clipboard} unmountOnExit>
          <Paper
            elevation={8}
            sx={{
              p: 1.5,
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'background.paper',
              borderTop: 2,
              borderColor: 'primary.main'
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" noWrap>
                Copied from <strong>{clipboard?.sourceLabel}</strong>
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {clipboardSummary}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentPasteGoIcon />}
              onClick={() => setBulkPasteOpen(true)}
            >
              Paste to...
            </Button>
            <Button size="small" onClick={() => setClipboard(null)}>
              Clear
            </Button>
          </Paper>
        </Fade>
      </Box>

      <Dialog
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        maxWidth="xs"
      >
        <DialogTitle>Remove Test Point?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the item script from the test points list. The item
            script will not be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
          <Button
            onClick={handleConfirmRemove}
            color="error"
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!pasteTarget}
        onClose={() => setPasteTarget(null)}
        maxWidth="xs"
      >
        <DialogTitle>Overwrite Steps?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will overwrite the existing steps in{' '}
            <strong>
              {pasteTarget ? parseStepsFromScript(pasteTarget).label : ''}
            </strong>
            . Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasteTarget(null)} disabled={isPasting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPaste}
            color="warning"
            variant="contained"
            disabled={isPasting}
          >
            {isPasting ? 'Pasting...' : 'Overwrite'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkRemoveOpen}
        onClose={() => setBulkRemoveOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>Remove {selectedIds.size} Test Points?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove {selectedIds.size} item scripts from the test
            points list. The item scripts will not be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRemoveOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmBulkRemove}
            color="error"
            variant="contained"
          >
            Remove All
          </Button>
        </DialogActions>
      </Dialog>

      <BulkPasteDialog
        open={bulkPasteOpen}
        clipboard={clipboard}
        allScripts={childScripts}
        sourceScriptId={clipboard?.sourceScriptId ?? ''}
        isPasting={isPasting}
        onClose={() => setBulkPasteOpen(false)}
        onPaste={handleBulkPaste}
      />
    </>
  );
};
